import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, useApp } from "../../context/AppContext";
import { redirectToModuleSSO } from "../../utils/sso";
import IntegrationStatsDetailModal from "../../components/common/IntegrationStatsDetailModal";
import { HelpTooltip, ModuleLockedOverlay } from "../../components/common/UI";
import {
  scoreLabel,
  scoreColor,
} from "../../components/common/gapAssessmentTheme";
import {
  Building2,
  Users,
  Briefcase,
  ShieldCheck,
  RefreshCw,
  ChevronRight,
  CheckCircle2,
  Clock,
  Cookie,
  FileText,
  Lock,
  BarChart2,
  ScrollText,
  Handshake,
  ClipboardList,
  ClipboardCheck,
  ShieldPlus,
  ShieldAlert,
  TrendingUp,
  LayoutList,
  ListChecks,
  Target,
  AlertTriangle,
  Database,
  Mail,
  Phone,
  CreditCard,
  IdCard,
} from "lucide-react";

const MODULE_LABELS = {
  gap: "Gap Assessment",
  ropa: "ROPA",
  dpia: "PIA",
  tprm: "TPRM",
  dsar: "DSAR",
  dspm: "DSPM",
  consent: "Consent",
};

/** How often the dashboard silently re-fetches breach stats / recent incidents. */
const BREACH_LIVE_POLL_MS = 5000;

function sumStatValues(...vals) {
  return vals.reduce((sum, v) => sum + (Number(v) || 0), 0);
}

/**
 * Compact number formatting for card stats — e.g. 1000 -> "1k", 12500 -> "12.5k",
 * 2400000 -> "2.4m", 1000000000 -> "1b". Anything under 1000 is shown as-is.
 * Used everywhere a raw count is rendered in a dashboard card so big numbers
 * never blow out a card's layout.
 */
function formatCompactNumber(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  if (abs < 1000) return `${sign}${abs}`;

  const UNITS = [
    { threshold: 1e12, suffix: "t" },
    { threshold: 1e9, suffix: "b" },
    { threshold: 1e6, suffix: "m" },
    { threshold: 1e3, suffix: "k" },
  ];
  const idx = UNITS.findIndex((u) => abs >= u.threshold);
  const unit = UNITS[idx];
  const scaled = abs / unit.threshold;
  // One decimal place under 100 (e.g. "12.5k"), whole numbers at/above 100 (e.g. "125k")
  let rounded =
    scaled >= 100 ? Math.round(scaled) : Math.round(scaled * 10) / 10;
  // Rounding can push a value up to the next unit's threshold (e.g. 999,999 -> "1000k")
  // — bump to the next unit in that case so it reads as "1m" instead.
  if (rounded >= 1000 && idx > 0) {
    return formatCompactNumber(sign === "-" ? -abs : abs);
  }
  return `${sign}${rounded}${unit.suffix}`;
}

/** Header chip: open incidents, with the open agent count called out on its own. */
function formatBreachLiveLabel(stats) {
  const active = stats?.active ?? 0;
  const agentActive = stats?.agentActive ?? 0;
  if (active <= 0 && agentActive <= 0) return "No active breaches";
  const parts = [`${formatCompactNumber(active)} active`];
  if (agentActive > 0) {
    parts.push(`${formatCompactNumber(agentActive)} agent`);
  }
  // critical_high already includes every agent incident (they are High).
  // Show only the manual remainder so the agent figure is not repeated.
  const manualCritical = Math.max(
    0,
    (stats?.criticalHigh ?? 0) - (stats?.agentTotal ?? 0),
  );
  if (manualCritical > 0) {
    parts.push(`${formatCompactNumber(manualCritical)} critical`);
  }
  return parts.join(" · ");
}

/** Use API total when consistent; otherwise fall back to sum of parts. */
function effectiveStatTotal(apiTotal, ...parts) {
  const sum = sumStatValues(...parts);
  const api = Number(apiTotal) || 0;
  return Math.max(api, sum);
}

function formatStatPct(value, total) {
  const t = Number(total) || 0;
  const v = Number(value) || 0;
  if (t <= 0) return "0%";
  return `${Math.min(100, Math.round((v / t) * 100))}%`;
}

function progressColor(pct) {
  return scoreColor(Number(pct) || 0);
}

function sectionsColor(completed, total) {
  const t = Number(total) || 0;
  const c = Number(completed) || 0;
  if (t <= 0) return "#64748b";
  return progressColor(Math.round((c / t) * 100));
}

function gapsColor(count) {
  return (Number(count) || 0) > 0 ? "#ef4444" : "#22c55e";
}

function submittedColor(count) {
  return (Number(count) || 0) > 0 ? "#22c55e" : "#64748b";
}

const CircularProgress = ({ total, size = 90, strokeWidth = 9, segments }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const cx = size / 2;
  const cy = size / 2;
  const segmentSum = segments.reduce(
    (s, seg) => s + (Number(seg.value) || 0),
    0,
  );
  const safeTotal = total > 0 ? total : segmentSum || 1;
  let offset = 0;
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
      <circle
        cx={cx}
        cy={cy}
        r={radius}
        fill="none"
        stroke="#cbd5e1"
        strokeWidth={strokeWidth}
      />
      {segments.map((seg, i) => {
        const dash = ((Number(seg.value) || 0) / safeTotal) * circumference;
        const el = (
          <circle
            key={i}
            cx={cx}
            cy={cy}
            r={radius}
            fill="none"
            stroke={seg.color}
            strokeWidth={strokeWidth}
            strokeDasharray={`${dash} ${circumference}`}
            strokeDashoffset={-offset}
            strokeLinecap="round"
          />
        );
        offset += dash;
        return el;
      })}
    </svg>
  );
};

const cardShell =
  "bg-white rounded-xl border border-slate-200 p-4 flex flex-col gap-3 shadow-sm h-full";

function formatLastUsed(timestamp, { loading = false } = {}) {
  if (loading) return "Loading…";
  if (!timestamp) return "Not used yet";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "Not used yet";
  return `Last used: ${date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })}`;
}

const BetaBadge = () => (
  <span
    className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700 border border-amber-200/80 leading-none"
    title="Feature in progress"
  >
    β
  </span>
);

const CardHeader = ({
  title,
  subtitle,
  tooltip,
  iconEl,
  accentColor,
  onNavigate,
  headerExtra,
  badge,
  loading = false,
}) => (
  <div className="flex items-start justify-between">
    <div className="flex items-center gap-2.5 min-w-0">
      <div
        className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${accentColor}`}
      >
        {iconEl}
      </div>
      <div className="min-w-0">
        <p className="font-semibold text-slate-900 text-sm leading-tight">
          <span className="inline-flex items-center gap-1.5 flex-wrap">
            {title}
            {tooltip ? (
              <HelpTooltip content={tooltip} size={13} className="flex-shrink-0" />
            ) : null}
            {badge}
          </span>
        </p>
        <p className="text-xs text-slate-600 leading-tight">{subtitle}</p>
      </div>
    </div>
    {headerExtra || (
      <button
        onClick={onNavigate}
        disabled={loading}
        title="Open in module"
        className="w-7 h-7 rounded-full border border-slate-300 flex items-center justify-center text-slate-600 hover:bg-slate-100 transition-colors flex-shrink-0 disabled:opacity-60"
      >
        {loading ? (
          <RefreshCw size={12} className="animate-spin" />
        ) : (
          <ChevronRight size={13} />
        )}
      </button>
    )}
  </div>
);

const CardFooter = ({
  lastUsed,
  onNavigate,
  actionLabel = "View Details",
  actionClass = "text-blue-700 hover:text-blue-900",
}) => (
  <div className="flex items-center justify-between pt-2 border-t border-slate-200 mt-auto gap-2">
    <p className="text-[10px] text-slate-500 truncate min-w-0">{lastUsed}</p>
    {onNavigate ? (
      <button
        onClick={onNavigate}
        className={`text-xs font-medium flex items-center gap-0.5 ${actionClass}`}
      >
        {actionLabel} <ChevronRight size={11} />
      </button>
    ) : (
      <span
        className={`text-xs font-medium flex items-center gap-0.5 ${actionClass}`}
      >
        {actionLabel} <ChevronRight size={11} />
      </span>
    )}
  </div>
);

const CardBody = ({ children }) => (
  <div className="flex-1 min-h-[90px] flex items-center">{children}</div>
);

const DSPM_CANONICAL_FIELDS = [
  {
    key: "email",
    label: "Email Address",
    icon: Mail,
    color: "text-blue-600",
    match: (t) => /email|e-mail/i.test(t),
  },
  {
    key: "phone",
    label: "Phone",
    icon: Phone,
    color: "text-green-600",
    match: (t) => /phone|mobile|telephone/i.test(t),
  },
  {
    key: "pan",
    label: "PAN",
    icon: CreditCard,
    color: "text-purple-600",
    match: (t) => /^pan$/i.test(t) || /\bpan\b/i.test(t),
  },
  {
    key: "aadhaar",
    label: "Aadhaar",
    icon: IdCard,
    color: "text-orange-600",
    match: (t) => /aadhaar|aadhar|adhar|uid/i.test(t),
  },
];

const resolveDspmField = (dataType) => {
  const label = (dataType || "").trim();
  return DSPM_CANONICAL_FIELDS.find((f) => f.match(label));
};

const buildDspmItems = (dspmStats) => {
  const counts = {};
  for (const item of dspmStats?.payload || []) {
    const field = resolveDspmField(item.data_type);
    if (field) counts[field.key] = item.data_count ?? 0;
  }

  const hasApiData = dspmStats != null;

  return DSPM_CANONICAL_FIELDS.map((field) => {
    const Icon = field.icon;
    const count = hasApiData ? (counts[field.key] ?? 0) : 0;
    return {
      icon: <Icon size={14} className={field.color} />,
      label: field.label,
      value: formatCompactNumber(count),
    };
  });
};

const MetricList = ({ items }) => (
  <div className="w-full space-y-1.5">
    {items.map((item, i) => (
      <div
        key={i}
        className="flex items-center gap-2.5 py-1 border-b border-slate-100 last:border-0"
      >
        {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
        <span className="text-sm text-slate-700 flex-1">{item.label}</span>
        <span
          className="text-sm font-bold"
          style={{ color: item.valueColor || "#0f172a" }}
        >
          {typeof item.value === "number"
            ? formatCompactNumber(item.value)
            : item.value}
        </span>
      </div>
    ))}
  </div>
);

const IconMetricGrid = ({ items, loading }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full animate-pulse">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex flex-col items-center gap-1.5">
            <div className="flex items-center gap-1.5">
              <div className="w-5 h-5 bg-slate-100 rounded-full" />
              <div className="h-5 bg-slate-100 rounded w-8" />
            </div>
            <div className="h-3 bg-slate-100 rounded w-14" />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full">
      {items.map((item, i) => (
        <div key={i} className="flex flex-col items-center text-center gap-1">
          <div className="flex items-center justify-center gap-1.5">
            <span className="flex-shrink-0 flex items-center">{item.icon}</span>
            <p
              className="text-lg sm:text-xl font-bold leading-none"
              style={{ color: item.color }}
            >
              {typeof item.value === "number"
                ? formatCompactNumber(item.value)
                : item.value}
            </p>
          </div>
          <p className="text-[10px] text-slate-600 leading-tight">
            {item.label}
          </p>
        </div>
      ))}
    </div>
  );
};

const DonutCard = ({
  title,
  subtitle,
  tooltip,
  iconEl,
  accentColor,
  total,
  segments,
  lastUsed,
  loading = false,
  onArrowClick,
  arrowLoading = false,
  onViewDetails,
}) => (
  <div className={`${cardShell} relative`}>
    <CardHeader
      title={title}
      subtitle={subtitle}
      tooltip={tooltip}
      iconEl={iconEl}
      accentColor={accentColor}
      onNavigate={onArrowClick}
      loading={arrowLoading}
    />

    <CardBody>
      {loading ? (
        <div className="flex items-center gap-4 animate-pulse w-full">
          <div className="w-[90px] h-[90px] rounded-full bg-slate-100 flex-shrink-0" />
          <div className="flex flex-col gap-2 flex-1">
            <div className="h-3 bg-slate-100 rounded w-3/4" />
            <div className="h-3 bg-slate-100 rounded w-1/2" />
            <div className="h-3 bg-slate-100 rounded w-2/3" />
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-4 w-full">
          <div
            className="relative flex-shrink-0"
            style={{ width: 90, height: 90 }}
          >
            <CircularProgress
              total={total}
              size={90}
              strokeWidth={9}
              segments={segments}
            />
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <p className="text-lg font-bold text-slate-900 leading-none">
                {formatCompactNumber(total)}
              </p>
              <p className="text-[10px] text-slate-600">Total</p>
            </div>
          </div>
          <div className="flex flex-col gap-1.5 flex-1 min-w-0">
            {segments.map((s, i) => (
              <div key={i} className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ background: s.color }}
                  />
                  <span className="text-xs text-slate-700 truncate">
                    {s.label}
                  </span>
                </div>
                <span className="text-xs font-semibold text-slate-800 flex-shrink-0">
                  {typeof s.value === "number"
                    ? formatCompactNumber(s.value)
                    : s.value}{" "}
                  <span className="text-slate-500 font-normal">({s.pct})</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </CardBody>

    <CardFooter lastUsed={lastUsed} onNavigate={onViewDetails} />
  </div>
);

const EMPTY_SEGMENTS = (labels) =>
  labels.map(({ label, color }) => ({
    label,
    value: 0,
    pct: "0%",
    color,
  }));

export default function PrivacyHubDashboard() {
  const navigate = useNavigate();
  const { currentUser, showNotification } = useApp();
  const allowedModules =
    currentUser?.licensed_modules || currentUser?.modules || [];
  const isLocked = (key) => !allowedModules.includes(key);

  // ── Top stat cards overview ───────────────────────────────────────────────
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState("");

  // ── Live consent / cookie / DSAR stats ───────────────────────────────────
  const [consentStats, setConsentStats] = useState(null);
  const [cookieStats, setCookieStats] = useState(null);
  const [dsarStats, setDsarStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [dsarLoading, setDsarLoading] = useState(true);

  // ── Live PIA stats ────────────────────────────────────────────────────────
  const [piaStats, setPiaStats] = useState(null);
  const [piaLoading, setPiaLoading] = useState(true);

  // ── Live TPRM stats ───────────────────────────────────────────────────────
  const [tprmStats, setTprmStats] = useState(null);
  const [tprmLoading, setTprmLoading] = useState(true);

  // ── Live Gap Assessment stats ─────────────────────────────────────────────
  const [gapStats, setGapStats] = useState(null);
  const [gapLoading, setGapLoading] = useState(true);

  // ── Live ROPA stats ───────────────────────────────────────────────────────
  const [ropaStats, setRopaStats] = useState(null);
  const [ropaLoading, setRopaLoading] = useState(true);

  // ── Live DSPM stats ───────────────────────────────────────────────────────
  const [dspmStats, setDspmStats] = useState(null);
  const [dspmLoading, setDspmLoading] = useState(true);

  // ── Live breach updates (auto-polled into header chip) ────────────────────
  const [breachStats, setBreachStats] = useState(null);
  const [breachLoading, setBreachLoading] = useState(true);
  const [breachJustUpdated, setBreachJustUpdated] = useState(false);
  const breachPollInFlightRef = useRef(false);
  const breachSignatureRef = useRef("");
  const breachPulseTimerRef = useRef(null);

  const [detailView, setDetailView] = useState(null);

  // ── SSO hand-off ──────────────────────────────────────────────────────────
  // The arrow button on Consent, Cookie, DSAR and DSPM cards is dedicated to
  // SSO ONLY — it never opens the local stats modal. "View Details" (footer)
  // remains the only way to open that modal, wired separately below.
  const [ssoLoadingKey, setSsoLoadingKey] = useState(null);
  const openExternalModule = useCallback(
    async (moduleKey, cardKey, targetUrl) => {
      if (isLocked(moduleKey)) return;
      setSsoLoadingKey(cardKey);
      try {
        const redirected = await redirectToModuleSSO(moduleKey, { targetUrl });
        if (!redirected) {
          showNotification?.(
            "This module isn't connected yet. Ask your administrator to turn it on.",
            "error",
          );
        }
      } catch (err) {
        showNotification?.(err.message || "Could not start single sign-on", "error");
      } finally {
        setSsoLoadingKey(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allowedModules],
  );

  const fetchOverview = useCallback(async () => {
    setOverviewLoading(true);
    try {
      const res = await apiFetch("/dashboard/overview");
      if (res.ok) {
        setOverview(await res.json());
        return true;
      }
      setOverview(null);
      return false;
    } catch {
      setOverview(null);
      return false;
    } finally {
      setOverviewLoading(false);
    }
  }, []);

  const fetchConsentAndCookie = useCallback(async () => {
    setStatsLoading(true);
    try {
      const [cRes, kRes] = await Promise.all([
        apiFetch("/consent/stats"),
        apiFetch("/consent/cookie-stats"),
      ]);
      if (cRes.ok) setConsentStats(await cRes.json());
      if (kRes.ok) setCookieStats(await kRes.json());
    } catch {
      // keep prior values
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchDsarStats = useCallback(async () => {
    setDsarLoading(true);
    try {
      const res = await apiFetch("/dsar/stats");
      if (res.ok) setDsarStats(await res.json());
    } catch {
      // keep prior values
    } finally {
      setDsarLoading(false);
    }
  }, []);

  const fetchPiaStats = useCallback(async () => {
    setPiaLoading(true);
    try {
      const res = await apiFetch("/pia/stats");
      if (res.ok) {
        const data = await res.json();
        setPiaStats({
          total: data.total ?? 0,
          highRisk: data.high_risk ?? 0,
          avgScore: data.avg_score ?? null,
          lastUsedAt: data.last_used_at ?? null,
        });
      }
    } catch {
      // keep prior values
    } finally {
      setPiaLoading(false);
    }
  }, []);

  const fetchTprmStats = useCallback(async () => {
    setTprmLoading(true);
    try {
      const res = await apiFetch("/tprm/stats");
      if (res.ok) {
        const data = await res.json();
        setTprmStats({
          total: data.total ?? 0,
          submitted: data.submitted ?? 0,
          pending: data.pending ?? 0,
          highRisk: data.high_risk ?? 0,
          avgScore: data.avg_score ?? null,
          lastUsedAt: data.last_used_at ?? null,
        });
      }
    } catch {
      // keep prior values
    } finally {
      setTprmLoading(false);
    }
  }, []);

  const fetchGapStats = useCallback(async () => {
    setGapLoading(true);
    try {
      const res = await apiFetch("/gap-assessment/stats");
      if (res.ok) {
        const data = await res.json();
        setGapStats({
          sectionsTotal: data.sections_total ?? 0,
          sectionsCompleted: data.sections_completed ?? 0,
          answered: data.answered ?? 0,
          questionsTotal: data.questions_total ?? 0,
          gaps: data.gaps ?? 0,
          score: data.score ?? null,
          complianceLabel: data.compliance_label ?? null,
          submitted: data.submitted ?? false,
          hasDraft: data.has_draft ?? false,
          lastUsedAt: data.last_used_at ?? null,
        });
      }
    } catch {
      // keep prior values
    } finally {
      setGapLoading(false);
    }
  }, []);

  const fetchRopaStats = useCallback(async () => {
    setRopaLoading(true);
    try {
      const res = await apiFetch("/ropa/stats");
      if (res.ok) {
        const data = await res.json();
        setRopaStats({
          sectionsTotal: data.sections_total ?? 0,
          sectionsFilled: data.sections_filled ?? 0,
          progressPct: data.progress_pct ?? 0,
          submitted: data.submitted ?? 0,
          hasDraft: data.has_draft ?? false,
          status: data.status ?? "not_started",
          lastUsedAt: data.last_used_at ?? null,
        });
      }
    } catch {
      // keep prior values
    } finally {
      setRopaLoading(false);
    }
  }, []);

  const fetchDspmStats = useCallback(async () => {
    setDspmLoading(true);
    try {
      const res = await apiFetch("/dspm/stats");
      if (res.ok) setDspmStats(await res.json());
    } catch {
      // keep prior values
    } finally {
      setDspmLoading(false);
    }
  }, []);

  const fetchBreachLive = useCallback(async ({ silent = false } = {}) => {
    if (breachPollInFlightRef.current) return;
    if (isLocked("breach")) {
      setBreachLoading(false);
      return;
    }
    breachPollInFlightRef.current = true;
    if (!silent) setBreachLoading(true);
    try {
      const statsRes = await apiFetch("/breaches/stats");
      if (statsRes.ok) {
        const d = await statsRes.json();
        const nextStats = {
          total: d.total ?? 0,
          active: d.active ?? 0,
          agentTotal: d.agent_total ?? 0,
          agentActive: d.agent_active ?? 0,
          criticalHigh: d.critical_high ?? 0,
          lastUpdatedAt: d.last_updated_at ?? null,
        };
        setBreachStats(nextStats);

        const signature = [
          nextStats.total,
          nextStats.active,
          nextStats.agentTotal,
          nextStats.agentActive,
          nextStats.criticalHigh,
          nextStats.lastUpdatedAt ?? "",
        ].join("::");
        if (
          breachSignatureRef.current &&
          breachSignatureRef.current !== signature
        ) {
          setBreachJustUpdated(true);
          if (breachPulseTimerRef.current) {
            window.clearTimeout(breachPulseTimerRef.current);
          }
          breachPulseTimerRef.current = window.setTimeout(() => {
            setBreachJustUpdated(false);
          }, 2500);
        }
        breachSignatureRef.current = signature;
      }
    } catch {
      // keep prior values on transient poll failures
    } finally {
      setBreachLoading(false);
      breachPollInFlightRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedModules]);

  const refreshAll = useCallback(async () => {
    setRefreshing(true);
    setLoadError("");
    try {
      const results = await Promise.all([
        fetchOverview(),
        fetchConsentAndCookie(),
        fetchDsarStats(),
        fetchPiaStats(),
        fetchTprmStats(),
        fetchGapStats(),
        fetchRopaStats(),
        fetchDspmStats(),
        fetchBreachLive({ silent: false }),
      ]);
      const overviewOk = results[0] === true;
      if (!overviewOk) {
        setLoadError(
          "Some figures on this page could not be updated. Please refresh and try again. If the issue continues, contact your administrator.",
        );
      }
      setLastRefreshed(new Date());
    } finally {
      setRefreshing(false);
    }
  }, [
    fetchOverview,
    fetchConsentAndCookie,
    fetchDsarStats,
    fetchPiaStats,
    fetchTprmStats,
    fetchGapStats,
    fetchRopaStats,
    fetchDspmStats,
    fetchBreachLive,
  ]);

  useEffect(() => {
    document.body.style.margin = "0";
    document.body.style.padding = "0";
    return () => {
      document.body.style.margin = "";
      document.body.style.padding = "";
    };
  }, []);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  // Silent live polling for breach incidents — no manual refresh required.
  useEffect(() => {
    if (isLocked("breach")) return undefined;

    const poll = () => {
      if (!document.hidden) fetchBreachLive({ silent: true });
    };
    const interval = window.setInterval(poll, BREACH_LIVE_POLL_MS);
    const onVisible = () => {
      if (!document.hidden) fetchBreachLive({ silent: true });
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      if (breachPulseTimerRef.current) {
        window.clearTimeout(breachPulseTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchBreachLive, allowedModules]);

  const consentAccepted = consentStats?.accepted ?? 0;
  const consentRevoked = consentStats?.revoked ?? 0;
  const consentDeclined = consentStats?.declined ?? 0;
  const consentEffectiveTotal = consentStats
    ? effectiveStatTotal(
        consentStats.total,
        consentAccepted,
        consentRevoked,
        consentDeclined,
      )
    : 0;

  const cookieAccepted = cookieStats?.accepted ?? 0;
  const cookieRejected = cookieStats?.rejected ?? 0;
  const cookieEffectiveTotal = cookieStats
    ? effectiveStatTotal(cookieStats.total, cookieAccepted, cookieRejected)
    : 0;
  const cookiePending = cookieStats
    ? Math.max(0, cookieEffectiveTotal - cookieAccepted - cookieRejected)
    : 0;

  // Build donut card segments from live data (fallback to demo if loading)
  const consentSegments = consentStats
    ? [
        {
          label: "Accepted",
          value: consentAccepted,
          pct: formatStatPct(consentAccepted, consentEffectiveTotal),
          color: "#22c55e",
        },
        {
          label: "Revoked",
          value: consentRevoked,
          pct: formatStatPct(consentRevoked, consentEffectiveTotal),
          color: "#ef4444",
        },
        {
          label: "Declined",
          value: consentDeclined,
          pct: formatStatPct(consentDeclined, consentEffectiveTotal),
          color: "#f59e0b",
        },
      ]
    : EMPTY_SEGMENTS([
        { label: "Accepted", color: "#22c55e" },
        { label: "Revoked", color: "#ef4444" },
        { label: "Declined", color: "#f59e0b" },
      ]);

  const cookieSegments = cookieStats
    ? [
        {
          label: "Accepted",
          value: cookieAccepted,
          pct: formatStatPct(cookieAccepted, cookieEffectiveTotal),
          color: "#22c55e",
        },
        {
          label: "Rejected",
          value: cookieRejected,
          pct: formatStatPct(cookieRejected, cookieEffectiveTotal),
          color: "#f59e0b",
        },
        {
          label: "Pending",
          value: cookiePending,
          pct:
            cookiePending > 0
              ? formatStatPct(cookiePending, cookieEffectiveTotal)
              : "0%",
          color: "#64748b",
        },
      ]
    : EMPTY_SEGMENTS([
        { label: "Accepted", color: "#22c55e" },
        { label: "Rejected", color: "#f59e0b" },
        { label: "Pending", color: "#64748b" },
      ]);

  const consentLastUsed = formatLastUsed(consentStats?.updated_at, {
    loading: statsLoading,
  });

  const cookieLastUsed = formatLastUsed(cookieStats?.updated_at, {
    loading: statsLoading,
  });

  const dsarProcessed = dsarStats?.processed ?? 0;
  const dsarPending = dsarStats?.pending ?? 0;
  const dsarEffectiveTotal = dsarStats
    ? effectiveStatTotal(dsarStats.total, dsarProcessed, dsarPending)
    : 0;
  const dsarRejected = dsarStats
    ? Math.max(0, dsarEffectiveTotal - dsarProcessed - dsarPending)
    : 0;

  const dsarSegments = dsarStats
    ? [
        {
          label: "Processed",
          value: dsarProcessed,
          pct: formatStatPct(dsarProcessed, dsarEffectiveTotal),
          color: "#22c55e",
        },
        {
          label: "Pending",
          value: dsarPending,
          pct: formatStatPct(dsarPending, dsarEffectiveTotal),
          color: "#f59e0b",
        },
        {
          label: "Rejected",
          value: dsarRejected,
          pct: formatStatPct(dsarRejected, dsarEffectiveTotal),
          color: "#ef4444",
        },
      ]
    : EMPTY_SEGMENTS([
        { label: "Processed", color: "#22c55e" },
        { label: "Pending", color: "#f59e0b" },
        { label: "Rejected", color: "#ef4444" },
      ]);

  const dsarLastUsed = formatLastUsed(dsarStats?.updated_at, {
    loading: dsarLoading,
  });

  const complianceScore = overview?.compliance_score ?? null;
  const complianceLabel =
    overview?.compliance_label ??
    (complianceScore != null ? scoreLabel(complianceScore) : null);

  const orgName =
    overview?.organization_name || currentUser?.organization_name || "—";

  const activeUsers = overview?.active_users ?? 0;
  const dataRequests = overview?.data_requests ?? (dsarEffectiveTotal || 0);

  const statCards = [
    {
      icon: <Building2 size={16} className="text-blue-700" />,
      bg: "bg-blue-50",
      label: "Organization",
      tooltip:
        "The organisation this dashboard belongs to. Active means the account can use the platform.",
      value: overviewLoading ? "…" : orgName,
      badge:
        currentUser?.status === "active"
          ? { text: "Active", cls: "bg-emerald-100 text-emerald-800" }
          : null,
      loading: overviewLoading,
    },
    {
      icon: <Users size={16} className="text-violet-700" />,
      bg: "bg-violet-50",
      label: "Active Users",
      tooltip:
        "People in your organisation whose accounts are currently active and can sign in.",
      value: overviewLoading ? "…" : formatCompactNumber(activeUsers),
      sub: overviewLoading ? null : "in your organization",
      subCls: "text-slate-600",
      loading: overviewLoading,
    },
    {
      icon: <Briefcase size={16} className="text-orange-700" />,
      bg: "bg-orange-50",
      label: "Data Requests",
      tooltip: (
        <span className="block">
          <span className="block mb-1.5">
            This number adds two totals. Consent choices (yes or no to using
            personal data) come from the Consent Management module. Requests to
            see, correct, or delete that data come from the DSAR module.
          </span>
          <span className="block">
            The orange text is only the DSAR requests that are still waiting
            for a reply.
          </span>
        </span>
      ),
      value: overviewLoading ? "…" : formatCompactNumber(dataRequests),
      sub:
        !overviewLoading && dsarPending > 0
          ? `${formatCompactNumber(dsarPending)} pending`
          : null,
      subCls: "text-orange-700",
      loading: overviewLoading,
    },
    {
      icon: <ShieldCheck size={16} className="text-indigo-700" />,
      bg: "bg-indigo-50",
      label: "Compliance Score",
      tooltip:
        "How ready your departments are. This averages each department’s most recent gap assessment. A higher percentage means fewer gaps.",
      value:
        overviewLoading
          ? "…"
          : complianceScore != null
            ? `${complianceScore}%`
            : "—",
      sub: complianceLabel || null,
      subCls: `font-medium ${complianceScore != null ? "" : "text-slate-500"}`,
      subStyle:
        complianceScore != null
          ? { color: progressColor(complianceScore) }
          : undefined,
      loading: overviewLoading,
    },
  ];

  const donutCards = [
    {
      title: "User Consent Management",
      subtitle: "Manage user consents and preferences",
      tooltip:
        "Counts sent from the Consent Management module: how people responded when asked to use their personal data — accepted, later revoked, or declined.",
      iconEl: <Users size={15} className="text-blue-700" />,
      accentColor: "bg-blue-50",
      total: consentEffectiveTotal,
      lastUsed: consentLastUsed,
      loading: statsLoading,
      segments: consentSegments,
      // Arrow (header) -> SSO hand-off into the external Consent module.
      onArrowClick: () => openExternalModule("consent", "consent"),
      arrowLoading: ssoLoadingKey === "consent",
      // "View Details" (footer) -> local stats modal, unchanged.
      onViewDetails: () => allowedModules.includes("consent") && setDetailView("consent"),
      moduleKey: "consent",
    },
    {
      title: "Cookie Consent Management",
      subtitle: "Manage cookie consents and preferences",
      tooltip:
        "Cookie banner choices on your sites: accepted, rejected, or still waiting for a choice.",
      iconEl: <Cookie size={15} className="text-orange-600" />,
      accentColor: "bg-orange-50",
      total: cookieEffectiveTotal,
      lastUsed: cookieLastUsed,
      loading: statsLoading,
      segments: cookieSegments,
      onArrowClick: () => openExternalModule("consent", "cookie", "/cookies"),
      arrowLoading: ssoLoadingKey === "cookie",
      onViewDetails: () => allowedModules.includes("consent") && setDetailView("cookie"),
      moduleKey: "consent",
    },
    {
      title: "Data Subject Access Requests",
      subtitle: "Handle data subject access requests",
      tooltip:
        "Counts sent from the DSAR module: requests to access, correct, or delete personal data, and how many are still open.",
      iconEl: <ClipboardList size={15} className="text-blue-700" />,
      accentColor: "bg-blue-50",
      total: dsarEffectiveTotal,
      lastUsed: dsarLastUsed,
      loading: dsarLoading,
      segments: dsarSegments,
      onArrowClick: () => openExternalModule("dsar", "dsar"),
      arrowLoading: ssoLoadingKey === "dsar",
      onViewDetails: () => allowedModules.includes("dsar") && setDetailView("dsar"),
      moduleKey: "dsar",
    },
  ];

  const detailModalConfig = useMemo(() => {
    if (detailView === "consent") {
      const total = consentEffectiveTotal;
      return {
        title: "User Consent Management",
        subtitle: "Detailed consent preference statistics",
        icon: <Users size={22} />,
        accent: {
          headerBg: "linear-gradient(135deg, #eff6ff, #dbeafe)",
          iconBg: "#2563eb",
          summaryBg: "#eff6ff",
          summaryBorder: "#bfdbfe",
          summaryText: "#1e40af",
        },
        stats: {
          hasData: consentStats != null && total > 0,
          alwaysShow: false,
          total,
          summaryLabel: "Total Consent Events",
          updatedAt: consentStats?.updated_at,
        },
        meta: [
          { label: "Module", value: consentStats?.module || "CONSENT" },
          {
            label: "Accepted",
            value: formatCompactNumber(consentStats?.accepted ?? 0),
          },
          {
            label: "Declined",
            value: formatCompactNumber(consentStats?.declined ?? 0),
          },
        ],
        rows: [
          {
            label: "Accepted",
            value: consentStats?.accepted ?? 0,
            color: "#22c55e",
            icon: <CheckCircle2 size={16} className="text-green-600" />,
          },
          {
            label: "Revoked",
            value: consentStats?.revoked ?? 0,
            color: "#ef4444",
            icon: <AlertTriangle size={16} className="text-red-600" />,
          },
          {
            label: "Declined",
            value: consentStats?.declined ?? 0,
            color: "#f59e0b",
            icon: <Clock size={16} className="text-amber-600" />,
          },
        ],
      };
    }

    if (detailView === "cookie") {
      const total = cookieEffectiveTotal;
      return {
        title: "Cookie Consent Management",
        subtitle: "Detailed cookie consent statistics",
        icon: <Cookie size={22} />,
        accent: {
          headerBg: "linear-gradient(135deg, #fff7ed, #ffedd5)",
          iconBg: "#ea580c",
          summaryBg: "#fff7ed",
          summaryBorder: "#fed7aa",
          summaryText: "#9a3412",
        },
        stats: {
          hasData: cookieStats != null && total > 0,
          total,
          summaryLabel: "Total Cookie Events",
          updatedAt: cookieStats?.updated_at,
        },
        meta: [
          { label: "Module", value: cookieStats?.module || "COOKIE" },
          {
            label: "Accepted",
            value: formatCompactNumber(cookieStats?.accepted ?? 0),
          },
          {
            label: "Rejected",
            value: formatCompactNumber(cookieStats?.rejected ?? 0),
          },
        ],
        rows: [
          {
            label: "Accepted",
            value: cookieStats?.accepted ?? 0,
            color: "#22c55e",
            icon: <CheckCircle2 size={16} className="text-green-600" />,
          },
          {
            label: "Rejected",
            value: cookieStats?.rejected ?? 0,
            color: "#f59e0b",
            icon: <AlertTriangle size={16} className="text-amber-600" />,
          },
          {
            label: "Pending",
            value: cookiePending,
            color: "#64748b",
            icon: <Clock size={16} className="text-slate-500" />,
          },
        ],
      };
    }

    if (detailView === "dsar") {
      const total = dsarEffectiveTotal;
      return {
        title: "Data Subject Access Requests",
        subtitle: "DSAR processing overview",
        icon: <ClipboardList size={22} />,
        accent: {
          headerBg: "linear-gradient(135deg, #eff6ff, #dbeafe)",
          iconBg: "#2563eb",
          summaryBg: "#eff6ff",
          summaryBorder: "#bfdbfe",
          summaryText: "#1e40af",
        },
        stats: {
          hasData: dsarStats != null && total > 0,
          total,
          summaryLabel: "Total DSAR Requests",
          updatedAt: dsarStats?.updated_at,
        },
        meta: [
          { label: "Module", value: dsarStats?.module || "DSAR" },
          {
            label: "Processed",
            value: formatCompactNumber(dsarStats?.processed ?? 0),
          },
          {
            label: "Pending",
            value: formatCompactNumber(dsarStats?.pending ?? 0),
          },
        ],
        rows: [
          {
            label: "Processed",
            value: dsarStats?.processed ?? 0,
            color: "#22c55e",
            icon: <CheckCircle2 size={16} className="text-green-600" />,
          },
          {
            label: "Pending",
            value: dsarStats?.pending ?? 0,
            color: "#f59e0b",
            icon: <Clock size={16} className="text-amber-600" />,
          },
          {
            label: "Rejected / Other",
            value: dsarRejected,
            color: "#ef4444",
            icon: <AlertTriangle size={16} className="text-red-600" />,
          },
        ],
      };
    }

    if (detailView === "ropa") {
      const total = ropaStats?.sectionsTotal ?? 12;
      const filled = ropaStats?.sectionsFilled ?? 0;
      const remaining = Math.max(0, total - filled);
      const progressPct = ropaStats?.progressPct ?? 0;
      return {
        title: "ROPA",
        subtitle: "Records of Processing Activities — progress detail",
        icon: <FileText size={22} />,
        accent: {
          headerBg: "linear-gradient(135deg, #eef2ff, #e0e7ff)",
          iconBg: "#4f46e5",
          summaryBg: "#eef2ff",
          summaryBorder: "#c7d2fe",
          summaryText: progressColor(progressPct),
        },
        stats: {
          alwaysShow: true,
          total: ropaStats?.progressPct ?? 0,
          barTotal: total,
          summaryLabel: "Overall Progress (%)",
          updatedAt: ropaStats?.lastUsedAt,
        },
        meta: [
          {
            label: "Status",
            value: ropaStats?.status?.replace("_", " ") || "not started",
          },
          {
            label: "Submitted",
            value: formatCompactNumber(ropaStats?.submitted ?? 0),
          },
          { label: "Draft", value: ropaStats?.hasDraft ? "Yes" : "No" },
        ],
        rows: [
          {
            label: "Sections completed",
            value: filled,
            color: sectionsColor(filled, total),
            icon: <LayoutList size={16} className="text-indigo-600" />,
          },
          {
            label: "Sections remaining",
            value: remaining,
            color: remaining > 0 ? "#f59e0b" : "#22c55e",
            icon: <ListChecks size={16} className="text-slate-500" />,
          },
          {
            label: "Submissions",
            value: ropaStats?.submitted ?? 0,
            color: submittedColor(ropaStats?.submitted ?? 0),
            icon: <CheckCircle2 size={16} className="text-green-600" />,
          },
        ],
      };
    }

    if (detailView === "gap") {
      const total = gapStats?.questionsTotal ?? 0;
      const answered = gapStats?.answered ?? 0;
      const unanswered = Math.max(0, total - answered);
      const gapScore = gapStats?.score ?? 0;
      return {
        title: "Gap Assessment",
        subtitle: "DPDP compliance readiness breakdown",
        icon: <ClipboardCheck size={22} />,
        accent: {
          headerBg: "linear-gradient(135deg, #fff1f2, #ffe4e6)",
          iconBg: "#e11d48",
          summaryBg: "#fff1f2",
          summaryBorder: "#fecdd3",
          summaryText: progressColor(gapScore),
        },
        stats: {
          alwaysShow: true,
          total: gapStats?.score ?? 0,
          barTotal: total,
          summaryLabel: "Compliance Score (%)",
          updatedAt: gapStats?.lastUsedAt,
        },
        meta: [
          {
            label: "Label",
            value:
              gapStats?.complianceLabel ||
              (gapStats?.score != null ? scoreLabel(gapStats.score) : "—"),
          },
          {
            label: "Sections",
            value: `${gapStats?.sectionsCompleted ?? 0}/${gapStats?.sectionsTotal ?? 0}`,
          },
          { label: "Submitted", value: gapStats?.submitted ? "Yes" : "No" },
        ],
        rows: [
          {
            label: "Questions answered",
            value: answered,
            color: sectionsColor(answered, total),
            icon: <CheckCircle2 size={16} className="text-emerald-600" />,
          },
          {
            label: "Questions unanswered",
            value: unanswered,
            color: unanswered > 0 ? "#f59e0b" : "#22c55e",
            icon: <Clock size={16} className="text-slate-500" />,
          },
          {
            label: "Critical gaps",
            value: gapStats?.gaps ?? 0,
            color: gapsColor(gapStats?.gaps ?? 0),
            icon: <AlertTriangle size={16} className="text-rose-600" />,
          },
        ],
      };
    }

    if (detailView === "pia") {
      const total = piaStats?.total ?? 0;
      const highRisk = piaStats?.highRisk ?? 0;
      const lowRisk = Math.max(0, total - highRisk);
      const avgScore =
        piaStats?.avgScore != null ? `${piaStats.avgScore}%` : "—";
      return {
        title: "DPIA",
        subtitle: "Data Protection Impact Assessments",
        icon: <Lock size={22} />,
        accent: {
          headerBg: "linear-gradient(135deg, #faf5ff, #f3e8ff)",
          iconBg: "#7c3aed",
          summaryBg: "#faf5ff",
          summaryBorder: "#e9d5ff",
          summaryText: "#5b21b6",
        },
        stats: {
          alwaysShow: true,
          total,
          summaryLabel: "Total DPIA Submissions",
          updatedAt: piaStats?.lastUsedAt,
        },
        meta: [
          {
            label: "High Risk",
            value: formatCompactNumber(highRisk),
          },
          {
            label: "Low Risk",
            value: formatCompactNumber(lowRisk),
          },
          { label: "Avg. Score", value: avgScore },
        ],
        rows: [
          {
            label: "Total",
            value: total,
            color: "#2563eb",
            icon: <Database size={16} className="text-blue-600" />,
          },
          {
            label: "High Risk",
            value: highRisk,
            color: "#ef4444",
            icon: <AlertTriangle size={16} className="text-red-600" />,
          },
          {
            label: "Low Risk",
            value: lowRisk,
            color: "#22c55e",
            icon: <ShieldCheck size={16} className="text-green-600" />,
          },
          {
            label: "Avg. Score",
            value: avgScore,
            color: "#7c3aed",
            icon: <TrendingUp size={16} className="text-purple-600" />,
          },
        ],
      };
    }

    if (detailView === "tprm") {
      const total = tprmStats?.total ?? 0;
      const highRisk = tprmStats?.highRisk ?? 0;
      const lowRisk = Math.max(0, total - highRisk);
      return {
        title: "Third Parties",
        subtitle: "Monitor external vendor compliance",
        icon: <Handshake size={22} />,
        accent: {
          headerBg: "linear-gradient(135deg, #eff6ff, #dbeafe)",
          iconBg: "#2563eb",
          summaryBg: "#eff6ff",
          summaryBorder: "#bfdbfe",
          summaryText: "#1d4ed8",
        },
        stats: {
          alwaysShow: true,
          total,
          summaryLabel: "Total Vendors",
          updatedAt: tprmStats?.lastUsedAt,
        },
        meta: [
          {
            label: "Submitted",
            value: formatCompactNumber(tprmStats?.submitted ?? 0),
          },
          {
            label: "Pending",
            value: formatCompactNumber(tprmStats?.pending ?? 0),
          },
          {
            label: "Avg. score",
            value: tprmStats?.avgScore != null ? `${tprmStats.avgScore}%` : "—",
          },
        ],
        rows: [
          {
            label: "Total",
            value: total,
            color: "#2563eb",
            icon: <Users size={16} className="text-blue-600" />,
          },
          {
            label: "High Risk",
            value: highRisk,
            color: "#ef4444",
            icon: <AlertTriangle size={16} className="text-red-600" />,
          },
          {
            label: "Low Risk",
            value: lowRisk,
            color: "#22c55e",
            icon: <ShieldCheck size={16} className="text-green-600" />,
          },
          {
            label: "Avg. Score",
            value: tprmStats?.avgScore != null ? `${tprmStats.avgScore}%` : "—",
            color: "#9333ea",
            icon: <TrendingUp size={16} className="text-purple-600" />,
          },
        ],
      };
    }

    if (detailView === "privacy") {
      return {
        title: "Privacy Notices",
        badge: <BetaBadge />,
        subtitle: "Organizational disclosure management",
        icon: <ScrollText size={22} />,
        accent: {
          headerBg: "linear-gradient(135deg, #fffbeb, #fef3c7)",
          iconBg: "#d97706",
          summaryBg: "#fffbeb",
          summaryBorder: "#fde68a",
          summaryText: "#92400e",
        },
        stats: {
          hasData: false,
          alwaysShow: false,
          total: 0,
          summaryLabel: "Total Notices",
          updatedAt: null,
        },
        meta: [],
        rows: [],
      };
    }

    if (detailView === "dspm") {
      const total = dspmStats?.total_data_count ?? 0;
      const payload = dspmStats?.payload || [];
      return {
        title: "DSPM",
        subtitle: "Data discovery & classification summary",
        icon: <BarChart2 size={22} />,
        accent: {
          headerBg: "linear-gradient(135deg, #ecfdf5, #d1fae5)",
          iconBg: "#059669",
          summaryBg: "#ecfdf5",
          summaryBorder: "#a7f3d0",
          summaryText: "#047857",
        },
        stats: {
          hasData: dspmStats != null && total > 0,
          total,
          summaryLabel: "Total Sensitive Records",
          updatedAt: dspmStats?.received_at,
        },
        meta: [
          { label: "Module", value: dspmStats?.module || "DSPM" },
          { label: "Data Types", value: String(payload.length) },
          {
            label: "Record ID",
            value: dspmStats?.id != null ? String(dspmStats.id) : "—",
          },
        ],
        rows: payload.map((item) => {
          const field = resolveDspmField(item.data_type);
          const Icon = field?.icon || Database;
          return {
            label: field?.label || item.data_type,
            value: item.data_count ?? 0,
            color: "#10b981",
            icon: (
              <Icon size={16} className={field?.color || "text-emerald-600"} />
            ),
          };
        }),
      };
    }

    return null;
  }, [
    detailView,
    consentStats,
    cookieStats,
    cookiePending,
    dsarStats,
    dsarRejected,
    dspmStats,
    ropaStats,
    gapStats,
    piaStats,
    tprmStats,
  ]);

  const dspmItems = buildDspmItems(dspmStats);

  const dspmTotal = dspmStats?.total_data_count ?? 0;
  const dspmLastUsed = formatLastUsed(dspmStats?.received_at, {
    loading: dspmLoading,
  });

  const quickActionTourIds = {
    "/user/pia": "tour-dash-qa-pia",
    "/user/ropa": "tour-dash-qa-ropa",
    "/user/gap-assessment": "tour-dash-qa-gap",
    "/user/tprm": "tour-dash-qa-tprm",
    "/user/reports": "tour-dash-qa-reports",
  };

  const quickActionModules = {
    "/user/pia": "dpia",
    "/user/ropa": "ropa",
    "/user/gap-assessment": "gap",
    "/user/tprm": "tprm",
    "/user/reports": "reports",
  };

  const quickActions = [
    {
      icon: <ShieldPlus size={18} />,
      label: "Create DPIA",
      path: "/user/pia",
      cls: "bg-purple-50 text-purple-800 hover:bg-purple-100",
    },
    {
      icon: <ScrollText size={18} />,
      label: "Add ROPA Record",
      path: "/user/ropa",
      cls: "bg-blue-50 text-blue-800 hover:bg-blue-100",
    },
    {
      icon: <ClipboardCheck size={18} />,
      label: "Start Gap Assessment",
      path: "/user/gap-assessment",
      cls: "bg-indigo-50 text-indigo-800 hover:bg-indigo-100",
    },
    {
      icon: <Handshake size={18} />,
      label: "Assess Third Party",
      path: "/user/tprm",
      cls: "bg-emerald-50 text-emerald-800 hover:bg-emerald-100",
    },
    {
      icon: <TrendingUp size={18} />,
      label: "Generate Report",
      path: "/user/reports",
      cls: "bg-orange-50 text-orange-800 hover:bg-orange-100",
    },
  ];

  return (
    <div className="bg-slate-100 font-sans -m-4 lg:-m-6">
      {/* Header */}
      <header
        data-tour-id="tour-dash-header"
        className="bg-white border-b border-slate-300 px-4 sm:px-6 py-3 flex items-center justify-between gap-3"
      >
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center shadow-md flex-shrink-0"
            style={{
              background: "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)",
            }}
          >
            <BarChart2 size={18} className="text-white" />
          </div>
          <div>
            <h1 className="text-base sm:text-lg font-bold text-slate-900 leading-tight">
              Dashboard
            </h1>
            <p className="text-xs text-slate-700 hidden sm:block">
              Key metrics and statistics at a glance
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          {!isLocked("breach") && (
            <button
              type="button"
              data-tour-id="tour-dash-breach-live"
              onClick={() => navigate("/user/breach-management")}
              title="Live breach updates, including agent incidents — auto-refreshes when counts change"
              className={`hidden sm:inline-flex items-center gap-1.5 max-w-[320px] px-2 py-1 rounded-lg border text-[11px] font-semibold transition-colors ${
                breachJustUpdated || (breachStats?.active ?? 0) > 0 || (breachStats?.agentActive ?? 0) > 0
                  ? "bg-red-50 border-red-200 text-red-800 hover:bg-red-100"
                  : "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100"
              }`}
            >
              <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
              </span>
              <ShieldAlert size={12} className="flex-shrink-0" />
              <span className="truncate">
                {breachLoading && !breachStats
                  ? "Breaches…"
                  : breachJustUpdated
                    ? "Breach updated"
                    : formatBreachLiveLabel(breachStats)}
              </span>
            </button>
          )}
          <p
            data-tour-id="tour-dash-last-refreshed"
            className="hidden md:flex items-center gap-1.5 text-xs text-slate-600"
          >
            <RefreshCw size={11} />{" "}
            {refreshing
              ? "Refreshing dashboard…"
              : lastRefreshed
                ? `Last refreshed: ${lastRefreshed.toLocaleString("en-IN", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                  })}`
                : "Loading dashboard…"}
          </p>
          <button
            type="button"
            data-tour-id="tour-dash-refresh"
            onClick={refreshAll}
            disabled={refreshing}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-white text-xs font-semibold rounded-lg transition-colors ${
              refreshing
                ? "bg-blue-500 cursor-wait opacity-90"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
            <span className="hidden sm:inline">
              {refreshing ? "Refreshing…" : "Refresh"}
            </span>
          </button>
        </div>
      </header>

      <div className="p-3 sm:p-4 space-y-3">
        {loadError && (
          <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-900 text-sm">
            <AlertTriangle
              size={18}
              className="flex-shrink-0 mt-0.5 text-amber-600"
            />
            <div>
              <p className="font-semibold">Dashboard figures are temporarily unavailable</p>
              <p className="text-xs mt-1 text-amber-800">{loadError}</p>
            </div>
          </div>
        )}
        {/* Stat cards */}
        <div
          data-tour-id="tour-dash-stats"
          className="grid grid-cols-2 lg:grid-cols-4 gap-3"
        >
          {statCards.map((c, i) => (
            <div
              key={i}
              className="bg-white rounded-xl border border-slate-200 px-3 py-3 flex items-center gap-3 shadow-sm"
            >
              <div
                className={`w-8 h-8 rounded-lg ${c.bg} flex items-center justify-center flex-shrink-0`}
              >
                {c.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1 min-w-0">
                  <p className="flex-1 min-w-0 text-[10px] text-slate-600 uppercase tracking-wide font-semibold truncate">
                    {c.label}
                  </p>
                  {c.tooltip ? (
                    <HelpTooltip
                      content={c.tooltip}
                      size={12}
                      position="bottom"
                      maxWidth={320}
                      className="flex-shrink-0"
                    />
                  ) : null}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <p className="text-lg font-bold text-slate-900 leading-tight">
                    {c.value}
                  </p>
                  {c.badge && (
                    <span
                      className={`px-1.5 py-0.5 text-[10px] rounded-full font-semibold ${c.badge.cls}`}
                    >
                      {c.badge.text}
                    </span>
                  )}
                </div>
                {c.sub && (
                  <p
                    className={`text-[10px] flex items-center gap-0.5 font-medium ${c.subCls || ""}`}
                    style={c.subStyle}
                  >
                    {c.subIcon}
                    {c.sub}
                  </p>
                )}
              </div>
              {c.extra && (
                <div className="flex-shrink-0 hidden sm:block">{c.extra}</div>
              )}
            </div>
          ))}
        </div>

        {/* Donut cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 items-stretch">
          {donutCards.map((d, i) => (
            <div
              key={i}
              data-tour-id={
                i === 0
                  ? "tour-dash-consent"
                  : i === 1
                    ? "tour-dash-cookie"
                    : "tour-dash-dsar"
              }
              className="relative"
            >
              {isLocked(d.moduleKey) && (
                <ModuleLockedOverlay moduleLabel={MODULE_LABELS[d.moduleKey]} />
              )}

              <DonutCard {...d} />
            </div>
          ))}
        </div>

        {/* ROPA + Gap Assessment + DSPM */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 items-stretch">
          <div
            data-tour-id="tour-dash-ropa"
            className={`${cardShell} relative`}
          >
            {isLocked("ropa") && <ModuleLockedOverlay moduleLabel="ROPA" />}
            <CardHeader
              title="ROPA"
              subtitle="Records of Processing Activities"
              tooltip="How complete your record of personal-data processing activities is, and whether it has been submitted."
              iconEl={<FileText size={15} className="text-indigo-700" />}
              accentColor="bg-indigo-50"
              onNavigate={() => !isLocked("ropa") && setDetailView("ropa")}
            />
            <CardBody>
              {ropaLoading ? (
                <div className="animate-pulse w-full space-y-2">
                  <div className="h-3 bg-slate-100 rounded w-full" />
                  <div className="h-3 bg-slate-100 rounded w-4/5" />
                  <div className="h-3 bg-slate-100 rounded w-3/5" />
                </div>
              ) : (
                <MetricList
                  items={[
                    {
                      icon: (
                        <LayoutList size={14} className="text-indigo-600" />
                      ),
                      label: "Sections",
                      value: ropaStats
                        ? `${ropaStats.sectionsFilled}/${ropaStats.sectionsTotal}`
                        : "0/12",
                      valueColor: ropaStats
                        ? sectionsColor(
                            ropaStats.sectionsFilled,
                            ropaStats.sectionsTotal,
                          )
                        : "#64748b",
                    },
                    {
                      icon: (
                        <TrendingUp size={14} className="text-indigo-500" />
                      ),
                      label: "Progress",
                      value: ropaStats ? `${ropaStats.progressPct}%` : "0%",
                      valueColor: progressColor(ropaStats?.progressPct ?? 0),
                    },
                    {
                      icon: (
                        <CheckCircle2 size={14} className="text-indigo-700" />
                      ),
                      label: "Submitted",
                      value: ropaStats?.submitted ?? 0,
                      valueColor: submittedColor(ropaStats?.submitted ?? 0),
                    },
                  ]}
                />
              )}
            </CardBody>
            <CardFooter
              lastUsed={formatLastUsed(ropaStats?.lastUsedAt, {
                loading: ropaLoading,
              })}
              onNavigate={() => !isLocked("ropa") && setDetailView("ropa")}
            />
          </div>

          <div data-tour-id="tour-dash-gap" className={`${cardShell} relative`}>
            {isLocked("gap") && (
              <ModuleLockedOverlay moduleLabel="Gap Assessment" />
            )}
            <CardHeader
              title="Gap Assessment"
              subtitle="DPDP compliance readiness review"
              tooltip="Progress on your DPDP readiness review, the compliance score, and how many gaps are still open."
              iconEl={<ClipboardCheck size={15} className="text-rose-700" />}
              accentColor="bg-rose-50"
              onNavigate={() => !isLocked("gap") && setDetailView("gap")}
            />
            <CardBody>
              {gapLoading ? (
                <div className="animate-pulse w-full space-y-2">
                  <div className="h-3 bg-slate-100 rounded w-full" />
                  <div className="h-3 bg-slate-100 rounded w-4/5" />
                  <div className="h-3 bg-slate-100 rounded w-3/5" />
                </div>
              ) : (
                <MetricList
                  items={[
                    {
                      icon: <ListChecks size={14} className="text-rose-600" />,
                      label: "Sections",
                      value: gapStats
                        ? `${gapStats.sectionsCompleted}/${gapStats.sectionsTotal}`
                        : "0/7",
                      valueColor: gapStats
                        ? sectionsColor(
                            gapStats.sectionsCompleted,
                            gapStats.sectionsTotal,
                          )
                        : "#64748b",
                    },
                    {
                      icon: <Target size={14} className="text-rose-500" />,
                      label: "Score",
                      value:
                        gapStats?.score != null ? `${gapStats.score}%` : "—",
                      valueColor:
                        gapStats?.score != null
                          ? progressColor(gapStats.score)
                          : "#64748b",
                    },
                    {
                      icon: (
                        <AlertTriangle size={14} className="text-rose-700" />
                      ),
                      label: "Gaps",
                      value: gapStats?.gaps ?? 0,
                      valueColor: gapsColor(gapStats?.gaps ?? 0),
                    },
                  ]}
                />
              )}
            </CardBody>
            <CardFooter
              lastUsed={formatLastUsed(gapStats?.lastUsedAt, {
                loading: gapLoading,
              })}
              onNavigate={() => !isLocked("gap") && setDetailView("gap")}
            />
          </div>

          <div
            data-tour-id="tour-dash-dspm"
            className={`${cardShell} relative`}
          >
            {isLocked("dspm") && <ModuleLockedOverlay moduleLabel="DSPM" />}
            <CardHeader
              title="DSPM"
              subtitle="Discover, classify and protect data"
              tooltip="Sensitive data the scanner has found, such as email addresses, phone numbers, and identity numbers."
              iconEl={<BarChart2 size={15} className="text-emerald-700" />}
              accentColor="bg-emerald-50"
              headerExtra={
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className="text-[10px] font-bold text-emerald-800 bg-emerald-50 border border-emerald-300 px-1.5 py-0.5 rounded-md">
                    {dspmLoading ? "…" : formatCompactNumber(dspmTotal)}
                  </span>
                  {/* Arrow -> SSO hand-off into the external DSPM module */}
                  <button
                    onClick={() => openExternalModule("dspm", "dspm")}
                    disabled={ssoLoadingKey === "dspm"}
                    title="Open in module"
                    className="w-7 h-7 rounded-full border border-slate-300 flex items-center justify-center text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-60"
                  >
                    {ssoLoadingKey === "dspm" ? (
                      <RefreshCw size={12} className="animate-spin" />
                    ) : (
                      <ChevronRight size={13} />
                    )}
                  </button>
                </div>
              }
            />
            <CardBody>
              {dspmLoading ? (
                <div className="animate-pulse w-full space-y-2">
                  <div className="h-3 bg-slate-100 rounded w-full" />
                  <div className="h-3 bg-slate-100 rounded w-4/5" />
                  <div className="h-3 bg-slate-100 rounded w-3/5" />
                  <div className="h-3 bg-slate-100 rounded w-2/5" />
                </div>
              ) : (
                <MetricList items={dspmItems} />
              )}
            </CardBody>
            <CardFooter
              lastUsed={dspmLastUsed}
              onNavigate={() => !isLocked("dspm") && setDetailView("dspm")}
            />
          </div>
        </div>

        {/* DPIA + Third Parties + Privacy Notices */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 items-stretch">
          <div data-tour-id="tour-dash-pia" className={`${cardShell} relative`}>
            {isLocked("dpia") && <ModuleLockedOverlay moduleLabel="PIA" />}
            <CardHeader
              title="DPIA"
              subtitle="Data Protection Impact Assessments"
              tooltip="How many privacy impact assessments exist, how many are high risk, and the average score."
              iconEl={<Lock size={15} className="text-purple-700" />}
              accentColor="bg-purple-50"
              onNavigate={() => !isLocked("dpia") && setDetailView("pia")}
            />
            <CardBody>
              <IconMetricGrid
                loading={piaLoading}
                items={[
                  {
                    icon: <Database size={18} className="text-blue-600" />,
                    label: "Total",
                    value: piaStats?.total ?? 0,
                    color: "#2563eb",
                  },
                  {
                    icon: <AlertTriangle size={18} className="text-red-600" />,
                    label: "High Risk",
                    value: piaStats?.highRisk ?? 0,
                    color: "#ef4444",
                  },
                  {
                    icon: <ShieldCheck size={18} className="text-green-600" />,
                    label: "Low Risk",
                    value: Math.max(
                      0,
                      (piaStats?.total ?? 0) - (piaStats?.highRisk ?? 0),
                    ),
                    color: "#22c55e",
                  },
                  {
                    icon: <TrendingUp size={18} className="text-purple-600" />,
                    label: "Avg. Score",
                    value:
                      piaStats?.avgScore != null
                        ? `${piaStats.avgScore}%`
                        : "—",
                    color: "#7c3aed",
                  },
                ]}
              />
            </CardBody>
            <CardFooter
              lastUsed={formatLastUsed(piaStats?.lastUsedAt, {
                loading: piaLoading,
              })}
              onNavigate={() => !isLocked("dpia") && setDetailView("pia")}
              actionLabel="View More"
              actionClass="text-purple-700 hover:text-purple-900"
            />
          </div>

          <div
            data-tour-id="tour-dash-tprm"
            className={`${cardShell} relative`}
          >
            {isLocked("tprm") && <ModuleLockedOverlay moduleLabel="TPRM" />}
            <CardHeader
              title="Third Parties"
              subtitle="Monitor external vendor compliance"
              tooltip="Vendor assessments: how many third parties you track, how many are high risk, and the average score."
              iconEl={<Handshake size={15} className="text-blue-700" />}
              accentColor="bg-blue-50"
              onNavigate={() => !isLocked("tprm") && setDetailView("tprm")}
            />
            <CardBody>
              <IconMetricGrid
                loading={tprmLoading}
                items={[
                  {
                    icon: <Users size={18} className="text-blue-600" />,
                    label: "Total",
                    value: tprmStats?.total ?? 0,
                    color: "#2563eb",
                  },
                  {
                    icon: <AlertTriangle size={18} className="text-red-600" />,
                    label: "High Risk",
                    value: tprmStats?.highRisk ?? 0,
                    color: "#ef4444",
                  },
                  {
                    icon: <ShieldCheck size={18} className="text-green-600" />,
                    label: "Low Risk",
                    value: Math.max(
                      0,
                      (tprmStats?.total ?? 0) - (tprmStats?.highRisk ?? 0),
                    ),
                    color: "#22c55e",
                  },
                  {
                    icon: <TrendingUp size={18} className="text-purple-600" />,
                    label: "Avg. Score",
                    value:
                      tprmStats?.avgScore != null
                        ? `${tprmStats.avgScore}%`
                        : "—",
                    color: "#9333ea",
                  },
                ]}
              />
            </CardBody>
            <CardFooter
              lastUsed={formatLastUsed(tprmStats?.lastUsedAt, {
                loading: tprmLoading,
              })}
              onNavigate={() => !isLocked("tprm") && setDetailView("tprm")}
              actionClass="text-blue-700 hover:text-blue-900"
            />
          </div>

          <div data-tour-id="tour-dash-privacy" className={cardShell}>
            <CardHeader
              title="Privacy Notices"
              badge={<BetaBadge />}
              subtitle="Manage organizational disclosures"
              tooltip="Organisational privacy notices, and whether they are still pending or already published. This feature is still in progress."
              iconEl={<ScrollText size={15} className="text-amber-700" />}
              accentColor="bg-amber-50"
              onNavigate={() => setDetailView("privacy")}
            />
            <CardBody>
              <MetricList
                items={[
                  {
                    icon: <FileText size={14} className="text-slate-600" />,
                    label: "Total",
                    value: "0",
                  },
                  {
                    icon: <Clock size={14} className="text-amber-600" />,
                    label: "Pending",
                    value: "0",
                  },
                  {
                    icon: (
                      <CheckCircle2 size={14} className="text-emerald-600" />
                    ),
                    label: "Published",
                    value: "0",
                  },
                ]}
              />
            </CardBody>
            <CardFooter
              lastUsed={formatLastUsed(null)}
              onNavigate={() => setDetailView("privacy")}
            />
          </div>
        </div>

        {/* Quick Actions */}
        <div
          data-tour-id="tour-dash-quick-actions"
          className="grid grid-cols-1 gap-3"
        >
          <div className={`${cardShell} min-h-0`}>
            <p className="font-semibold text-slate-900 text-sm mb-3">
              Quick Actions
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
              {quickActions.map((item) => (
                <button
                  key={item.path}
                  type="button"
                  data-tour-id={quickActionTourIds[item.path]}
                  onClick={() => {
                    const mod = quickActionModules[item.path];
                    if (mod && !allowedModules.includes(mod)) return;
                    navigate(item.path);
                  }}
                  className={`flex flex-col sm:flex-row items-center sm:justify-between gap-1.5 px-3 py-3 ${item.cls} rounded-xl transition-colors font-semibold text-sm group cursor-pointer`}
                >
                  <div className="flex flex-col sm:flex-row items-center gap-1.5">
                    {item.icon}
                    <span className="text-xs sm:text-sm text-center sm:text-left leading-tight">
                      {item.label}
                    </span>
                  </div>
                  <ChevronRight
                    size={13}
                    className="hidden sm:block opacity-60 group-hover:opacity-100 transition-opacity"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <IntegrationStatsDetailModal
        open={!!detailView}
        onClose={() => setDetailView(null)}
        config={detailModalConfig}
        loading={
          (detailView === "consent" && statsLoading) ||
          (detailView === "cookie" && statsLoading) ||
          (detailView === "dsar" && dsarLoading) ||
          (detailView === "dspm" && dspmLoading) ||
          (detailView === "ropa" && ropaLoading) ||
          (detailView === "gap" && gapLoading) ||
          (detailView === "pia" && piaLoading) ||
          (detailView === "tprm" && tprmLoading)
        }
      />
    </div>
  );
}
